import { assign, createMachine } from 'xstate';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { AuthenticationPromptType, Credentials, CredentialsEntity } from '../../models/credentials';
import { Provider, ProviderEntity } from '../../models/provider';
import { getBankIdAuthenticationType } from '../../utilities/provider-utils';
import { BankIdAuthenticationType } from '../../components/screens/AwaitingCredentialsScreen/organisms/AwaitingMobileBankIdAuthenticationContainer/organisms/BankIdAuthWithSupplementalContainer/BankIdAuthWithSupplementalContainer';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { createCredentialsPollerMachine as createCredentialsPollerMachineImport } from './credentialsPollerMachine';

export type BankIdXContext = {
  credentials: CredentialsEntity;
  selectedProvider: ProviderEntity;
  forcedPromptType?: AuthenticationPromptType;
  error?: ApplicationError;
};

type BankIdXServices = {
  pollCredentials: any;
};

type CreateBankIdMachineProps = {
  initialContext: BankIdXContext;
  initialState?: string;
  createCredentialsPollerMachine?: typeof createCredentialsPollerMachineImport;
};

export const createBankIdMachine = ({
  initialContext,
  initialState = 'decideForcedPromptType',
  createCredentialsPollerMachine = createCredentialsPollerMachineImport,
}: CreateBankIdMachineProps) =>
  createMachine(
    {
      predictableActionArguments: true,
      id: 'bank-id-machine',
      tsTypes: {} as import('./bankIdMachine.typegen').Typegen0,
      context: initialContext,
      schema: {
        context: {} as BankIdXContext,
        services: {} as BankIdXServices,
      },
      initial: initialState,
      states: {
        decideForcedPromptType: {
          always: [
            {
              cond: 'isForcedPromptTypeLocal',
              target: 'uiLocalAutoRedirect',
            },
            {
              cond: 'isForcedPromptTypeRemote',
              target: 'decideRemote',
            },
            {
              target: 'decideTarget',
            },
          ],
        },
        decideTarget: {
          always: [
            {
              target: 'uiSelectTarget',
              cond: 'isMobile',
            },
            {
              target: 'uiPollingRemoteWithTokenHandoff',
              cond: 'hasStartTokenAndIsHandoff',
            },
            {
              target: 'uiPollingRemoteWithToken',
              cond: 'hasStartToken',
            },
            {
              target: 'uiPollingRemoteWithoutToken',
            },
          ],
        },
        uiSelectTarget: {
          on: {
            EVENT_USER_CLICK_SELECT_REMOTE: [
              {
                target: 'decideRemote',
              },
            ],
            EVENT_USER_CLICK_SELECT_LOCAL: [
              {
                target: 'uiPollingLocal',
              },
            ],
            EVENT_USER_CLICK_BACK: [
              {
                target: 'uiBack',
              },
            ],
          },
        },
        decideRemote: {
          always: [
            {
              target: 'uiPollingRemoteWithTokenHandoff',
              cond: 'hasStartTokenAndIsHandoff',
            },
            {
              target: 'uiPollingRemoteWithToken',
              cond: 'hasStartToken',
            },
            {
              target: 'uiPollingRemoteWithoutToken',
            },
          ],
        },
        uiPollingRemoteWithoutToken: {
          invoke: {
            src: 'pollCredentials',
            onDone: [
              {
                cond: 'isCredentialsStatusChanged',
                actions: 'actionPersistCredentials',
                target: 'decideCredentialsErrorOrDone',
              },
              {
                actions: 'actionPersistCredentials',
                target: 'uiPollingRemoteWithoutToken',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'setError',
              },
            ],
          },
          on: {
            EVENT_USER_CLICK_BACK: [
              {
                target: 'uiBack',
              },
            ],
          },
        },
        uiPollingRemoteWithToken: {
          invoke: {
            src: 'pollCredentials',
            onDone: [
              {
                cond: 'isCredentialsStatusChanged',
                actions: 'actionPersistCredentials',
                target: 'decideCredentialsErrorOrDone',
              },
              {
                actions: 'actionPersistCredentials',
                target: 'uiPollingRemoteWithToken',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'setError',
              },
            ],
          },
        },
        uiPollingRemoteWithTokenHandoff: {
          invoke: {
            src: 'pollCredentials',
            onDone: [
              {
                cond: 'isCredentialsStatusChanged',
                actions: 'actionPersistCredentials',
                target: 'decideCredentialsErrorOrDone',
              },
              {
                actions: 'actionPersistCredentials',
                target: 'uiPollingRemoteWithTokenHandoff',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'setError',
              },
            ],
          },
          on: {
            EVENT_POLLER_ERROR: [
              {
                target: 'uiError',
              },
            ],
          },
        },
        uiLocalAutoRedirect: {
          on: {
            EVENT_AUTO_REDIRECT: [
              {
                target: 'uiPollingLocal',
              },
            ],
            EVENT_USER_CLICK_BACK: [
              {
                target: 'uiBack',
              },
            ],
          },
        },
        uiPollingLocal: {
          invoke: {
            src: 'pollCredentials',
            onDone: [
              {
                cond: 'isCredentialsStatusChanged',
                actions: 'actionPersistCredentials',
                target: 'decideCredentialsErrorOrDone',
              },
              {
                actions: 'actionPersistCredentials',
                target: 'uiPollingLocal',
              },
            ],
            onError: [
              {
                target: 'uiError',
                actions: 'setError',
              },
            ],
          },
          on: {
            EVENT_USER_CLICK_BACK: [
              {
                target: 'uiBack',
              },
            ],
          },
        },
        decideCredentialsErrorOrDone: {
          always: [
            {
              cond: 'isCredentialsError',
              target: 'uiError',
            },
            {
              target: 'uiDone',
            },
          ],
        },
        uiDone: {
          type: 'final',
        },
        uiError: {
          type: 'final',
        },
        uiBack: {
          type: 'final',
        },
      },
    },
    {
      guards: {
        isMobile: () => Boolean(userAgent.isMobile),
        hasStartTokenAndIsHandoff: ({ credentials }) =>
          Boolean(credentials.supplementalInformation) &&
          getBankIdAuthenticationType(credentials.providerName) ===
            BankIdAuthenticationType.BANK_ID_AUTO_START_TOKEN &&
          Provider.isRequiringHandoffQrCode(credentials.providerName),
        hasStartToken: ({ credentials }) => Boolean(credentials.supplementalInformation),
        isCredentialsStatusChanged: ({ credentials }, event) =>
          credentials.status !== event.data.status,
        isCredentialsError: ({ credentials }) => Credentials.hasError(credentials),
        isForcedPromptTypeLocal: ({ forcedPromptType }) =>
          forcedPromptType === AuthenticationPromptType.LOCAL,
        isForcedPromptTypeRemote: ({ forcedPromptType }) =>
          forcedPromptType === AuthenticationPromptType.REMOTE,
      },
      services: {
        pollCredentials: ({ credentials }) => createCredentialsPollerMachine({ credentials }),
      },
      actions: {
        setError: assign({
          error: (_, event) => event.data,
        }),
        actionPersistCredentials: assign({
          credentials: (_, event) => event.data,
        }),
      },
    }
  );
